// FinanceBoardPacket — the "Board Review" surface the Board walks at its
// semi-monthly meetings to confirm everything required for compliance/policy
// (FA.PL.001) is being reviewed. A live review checklist / status panel up top
// (QuickBooks connection, levy position, FY27 approval progress, pending /
// unbudgeted items, year-end allocation deadline, and an FA.PL.001 compliance
// checklist), then the clean, screen-share / print-friendly board summary
// (§12.1): cash position, budget-vs-actual, FY27 PROPOSED v2 budget (formal
// Board adoption pending) by committee vs the county base appropriation.
// Board/staff (view_finance).

import { useMemo } from "react";
import { Download, Printer } from "lucide-react";
import {
  useFinanceSummary,
  useFinanceHistory,
  useFinanceAvailable,
  useNonprofitSummary,
  useFy27Tracking,
  useFy27V2,
  type FinanceSummary,
} from "../hooks/useFinance";
import InfoTip from "../components/shared/InfoTip";
import PageIntro from "../components/shared/PageIntro";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import { FetchErrorBanner } from "../components/finance/ui";
import DownloadFinancePackageButton from "../components/finance/DownloadFinancePackageButton";
import StaleDataBanner from "../components/finance/StaleDataBanner";
import { asNum as n, usd0, usdC } from "../components/finance/charts";
import { exportCsv } from "../lib/csv";
import { ALLOCATION } from "../lib/financeConfig";
import {
  fundForFy,
  nextFiscalYear,
  fiscalCloseDeadlines,
  fyContextFromAsOf,
} from "../lib/fiscal";
import PriorYearBanner from "../components/finance/PriorYearBanner";

export default function FinanceBoardPacket() {
  const { data: summary, error: summaryError } = useFinanceSummary();
  const { data: history } = useFinanceHistory();
  const { data: availPair } = useFinanceAvailable();
  const { data: qbo } = useNonprofitSummary();
  const { data: tracking } = useFy27Tracking();
  const { data: fy27V2 } = useFy27V2();

  // Two ways to read funds available (captured authoritative pair). Render the
  // one-line reconciliation only when the row has loaded.
  const allObjectAvail =
    availPair?.all_object_available != null
      ? n(availPair.all_object_available)
      : null;
  const exprOnlyAvail =
    availPair?.expenditure_only_available != null
      ? n(availPair.expenditure_only_available)
      : null;
  const revenueGap =
    availPair?.revenue_net != null ? Math.abs(n(availPair.revenue_net)) : null;

  // ── C-4 first-paint guard ───────────────────────────────────────────────────
  // Every summary-derived figure is NULL until /api/finance/summary has actually
  // returned. These used to run through asNum(), whose 0 default painted a
  // confident board headline — "$0 available" in red (under the $100k
  // threshold), "$0 open encumbrance (0 POs)", "0% consumed" — during the
  // first-paint window (data null, error null, loading true), when the error
  // banner has nothing to disclose yet. Those zeros also reached the CSV. A null
  // now flows through to an explicit "not loaded" state and refuses the export.
  const figures = summary ? packetFigures(summary) : null;

  // FY labels derived from the data's as-of date: curFy = the position year
  // these actuals represent; nextFy = the year being budgeted (the "proposed"
  // column). Both roll forward together on 7/1 instead of being pinned to
  // FY26/FY27. The proposal data field is still `proposed_fy27` server-side, so
  // the label tracks the same offset the data carries.
  // curFy = the position year these actuals represent (from the data's as-of).
  // fyc tells us whether that's a PRIOR year vs the one we're in now (clock), so
  // the packet can flag "FY26 position (prior year)" instead of implying it's
  // today's. Fund is keyed to curFy so the fund segment matches the numbers.
  const fyc = fyContextFromAsOf(summary?.as_of_date ?? null);
  const curFy = fyc.dataFy ?? fyc.currentFy;
  // Budget label = the budget's OWN fiscal year (pinned in dash_fy27_v2_meta),
  // not clock-derived next-year — so the FY27 budget never reads "FY28" once the
  // position year rolls to FY27 on 7/1. Falls back to the clock offset.
  const nextFy = fy27V2?.meta?.budget_fy ?? nextFiscalYear(curFy);

  const fyRows = (history?.by_year ?? []).map((r) => ({
    fy: r.fiscal_year,
    budget: n(r.exp_budget),
    actual: n(r.exp_actual),
  }));

  // FY27 v2 budget — the 06-JUL revision (formal Board adoption pending; the
  // 7/6 minutes record no adoption motion — vote slated for the 7/20 meeting), served
  // live from dash_fy27_v2_* via useFy27V2(). Per-committee RECURRING operating
  // only (capital / funded projects / PO carryover are separate layers on the
  // FY27 Budget page). This replaces the retired trailing-3yr PO-average
  // heuristic (a pre-budget planning artifact superseded by the proposed v2).
  // The synthetic 'Unassigned' bucket always carries recurring = 0 — dropped so
  // the rows sum exactly to grand.recurring.
  const committeeRows = useMemo(() => {
    return (fy27V2?.committees ?? [])
      .filter((c) => !c.is_unassigned && n(c.recurring) > 0)
      .map((c) => ({ committee: c.committee, adopted: n(c.recurring) }))
      .sort((a, b) => b.adopted - a.adopted);
  }, [fy27V2]);

  const totalAdopted = n(fy27V2?.grand?.recurring);
  // Comparator = the county's loaded FY27 base appropriation. The v2 endpoint
  // serves no target field, so the shared ALLOCATION constant ($4,098,224,
  // re-anchored 7/19/2026 to the county base) is the single source here.
  const allocationTarget = ALLOCATION;
  const gap = totalAdopted - allocationTarget;

  // ── Review-panel live data (item 4) ─────────────────────────────────────────
  // Every value below either comes from a live endpoint or renders a clear
  // "pending" state — no fabricated numbers. A source that has not loaded says
  // so; it never degrades to "connected: no", "$0", or "none on record".
  const qboLoaded = qbo != null;
  const qboConnected = qbo?.connected ?? false;
  // Operating-fund-scoped encumbrance is the primary figure; FY26-carryover POs
  // (prior fund 2101, riding their OWN appropriation) are disclosed alongside,
  // never summed into the operating headline.
  const carryoverNote =
    figures && figures.carryoverEnc > 0
      ? ` + ${usdC(figures.carryoverEnc)} FY26-carryover POs (${figures.carryoverCount}, fund ${figures.carryoverFund}, own appropriation)`
      : "";

  // Levy-position checklist row. Without a loaded summary there is no position
  // to report — "pending / not loaded", never a $0 "warn".
  const levyStatus: ReviewStatus = !figures
    ? "pending"
    : figures.available < 100_000
      ? "warn"
      : "ok";
  const levyValue = figures
    ? `${usd0(figures.available)} available · ${usdC(figures.encumbrance)} open encumbrance (${figures.openPoCount} POs)${carryoverNote}`
    : summaryError
      ? "Not loaded — the levy summary failed to load; figures withheld"
      : "Not loaded — waiting on the levy summary; figures withheld";

  // Committee approval progress MUST come from the live v2 committee set
  // (dash_fy27_v2_committee, via fy27V2) — dash_fy27_committee (the v1
  // per-committee approval workflow, read via the retired useFy27Build) was
  // abandoned at the 2026-07-06 v2 restructure and its status field never
  // advances, so it read "0/8" forever. Mirrors financePackage.ts. Real
  // committee cards only (exclude the synthetic "Unassigned" bucket).
  const v2Committees = (fy27V2?.committees ?? []).filter(
    (c) => !c.is_unassigned,
  );
  const approvedCommittees =
    v2Committees.length > 0
      ? v2Committees.filter((c) => c.status === "approved").length
      : null;
  const totalCommittees = v2Committees.length > 0 ? v2Committees.length : null;
  // COMMITTEE-OWNER SIGN-OFF ONLY — not a Board adoption; that stays pending
  // (Frank, 2026-08-06). dash_fy27_v2_committee.status is the committee owner
  // marking their own sheet "approved". A Board adoption lives only in the
  // minutes (motion, second, vote); nothing in the v2 tables can show one, so
  // it stays pending here. Ten sign-offs therefore say exactly one thing:
  // committee sign-off is complete, formal Board adoption still pending.
  const committeeSignoffComplete =
    approvedCommittees != null &&
    totalCommittees != null &&
    totalCommittees > 0 &&
    approvedCommittees >= totalCommittees;

  // "None on record" is only true once the tracking endpoint has answered — an
  // unloaded list is unknown, not clean.
  const trackingLoaded = tracking != null;
  const unbudgetedItems = tracking?.unbudgeted ?? [];
  const unbudgetedTotal = n(tracking?.unbudgeted_total);

  // Close deadlines: the fiscal helper gives the current-FY fiscal-close date
  // (June 30) and the PWC year-end P-Card allocation deadline (June 24 — a fixed
  // county calendar date, surfaced as a deadline only, not a per-PO flag).
  // Close deadlines are ALWAYS clock-derived — they belong to the fiscal year we
  // are in NOW, not to the (possibly prior) year the data represents. Seeding
  // from the data's as-of date showed elapsed June-2026 deadlines in an FY27
  // packet. fiscalCloseDeadlines() with no arg → the current FY's June 30 / 24.
  const close = fiscalCloseDeadlines();

  // ── Packet readiness ────────────────────────────────────────────────────────
  // A packet output (CSV / print) may only be produced from sources that have
  // actually loaded. While any of them is null the actions are DISABLED — an
  // export or a print taken in the first-paint window would carry fabricated
  // zeros with no banner to warn the reader holding the paper.
  const missingSources = [
    summary == null ? "levy summary" : null,
    history == null ? "budget-vs-actual history" : null,
    fy27V2 == null ? `${nextFy} proposed budget` : null,
  ].filter((s): s is string => s != null);
  const packetReady = missingSources.length === 0;

  // Structured CSV of the packet's key figures — sectioned (Cash Position,
  // Budget vs Actual by FY, FY27 Proposed (v2) by Committee) so it can go to the
  // county/board as a file. Reuses the shared exportCsv util; figures come
  // straight from the validated endpoints already rendered on the page.
  function handleExportCsv() {
    // Belt and braces with the disabled button: never build an export from an
    // unloaded source, however the handler is reached.
    if (!packetReady) return;
    const built = buildBoardPacketCsv({
      summary,
      fyRows,
      committeeRows,
      totalAdopted,
      allocationTarget,
      curFy,
      nextFy,
    });
    if (!built) return;
    exportCsv(built.filename, built.rows, [
      { key: "section", header: "Section" },
      { key: "item", header: "Item" },
      { key: "value", header: "Value (USD or %)" },
      { key: "detail", header: "Detail" },
    ]);
  }

  return (
    <PageShell maxWidth="max-w-4xl">
      {/* Branded print-only header — renders only on paper / PDF (see the
          .print-header rules in index.css). Gives the board packet a clean
          title block: DCVFD/Ember mark, org name, report title, as-of date
          and fiscal-year scope. ember-light.png reads on white paper. */}
      <div className="print-header items-center gap-4 pb-4 mb-5 border-b-2 border-[#163832]">
        <img
          src="/brand/ember-light.png"
          alt="DCVFD Ember"
          className="h-14 w-auto object-contain"
        />
        <div className="min-w-0">
          <div className="text-[15px] font-black tracking-tight text-[#163832]">
            Dale City Volunteer Fire Department
          </div>
          <div className="text-[19px] font-bold text-black leading-tight">
            Board Review — Financial Package
          </div>
          <div className="text-[11px] text-gray-600 mt-0.5">
            {curFy}
            {fyc.isPriorYear ? " (prior yr)" : ""} position &amp; {nextFy}{" "}
            budget · fund {fundForFy(curFy)}
            {summary?.as_of_date ? ` · as of ${summary.as_of_date}` : ""} ·
            FA.PL.001 §12.1
          </div>
        </div>
      </div>

      {/* On-screen header + intro. Hidden on paper — the branded print
          header above replaces it so the PDF doesn't carry a duplicate title
          or the screen-only action buttons / explainer copy. */}
      <div className="no-print">
        <PageHeader
          eyebrow="FINANCE"
          title="DCVFD Finance — Board Review"
          subtitle={
            <>
              {curFy}
              {fyc.isPriorYear ? " (prior yr)" : ""} position &amp; {nextFy}{" "}
              budget · fund {fundForFy(curFy)}
              {summary?.as_of_date ? ` · as of ${summary.as_of_date}` : ""} ·
              FA.PL.001 §12.1
            </>
          }
          headerRight={
            <div className="no-print flex items-center gap-2">
              <button
                onClick={handleExportCsv}
                disabled={!packetReady}
                title={
                  packetReady
                    ? "Export the packet figures as CSV"
                    : `Disabled — ${missingSources.join(", ")} not loaded. An export now would carry fabricated zeros.`
                }
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-theme-overlay text-theme-primary transition ${
                  packetReady
                    ? "hover:bg-theme-overlay/70"
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <Download size={14} aria-hidden="true" />
                Export CSV
              </button>
              <DownloadFinancePackageButton format="xlsx" />
              <DownloadFinancePackageButton format="pdf" />
              <button
                onClick={() => window.print()}
                disabled={!packetReady}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-theme-overlay text-theme-primary transition ${
                  packetReady
                    ? "hover:bg-theme-overlay/70"
                    : "opacity-50 cursor-not-allowed"
                }`}
                title={
                  packetReady
                    ? "Quick browser print of this on-screen review checklist (the branded multi-section packet is the .pdf button)"
                    : `Disabled — ${missingSources.join(", ")} not loaded. Printing now would put unloaded figures on paper, where no banner can correct them.`
                }
              >
                <Printer size={14} aria-hidden="true" />
                Print page
              </button>
            </div>
          }
        />
        <PriorYearBanner
          ctx={fyc}
          asOf={summary?.as_of_date ?? null}
          className="mb-3"
        />
        <PageIntro>
          What the Board walks at each semi-monthly meeting: a live review
          checklist confirming we're reviewing everything FA.PL.001 requires,
          then the one-page summary — where our cash stands, how each year's
          budget landed against actuals, and the proposed {nextFy} budget by
          committee (formal Board adoption pending).
        </PageIntro>
      </div>

      <FetchErrorBanner error={summaryError} what="the board review" />

      {/* First-paint / partial-load disclosure. The error banner above only
          fires once a fetch has FAILED; this covers the window where sources
          are simply not back yet, during which the packet actions are disabled
          and the affected sections read "not loaded" instead of $0. */}
      {!packetReady && (
        <div
          role="status"
          className="no-print rounded-lg border border-status-amber/40 bg-status-amber/10 px-4 py-2.5 text-sm text-theme-primary"
        >
          <span className="font-semibold">Packet not ready.</span> Not loaded
          yet: {missingSources.join(", ")}. Export CSV and Print are disabled
          and the affected figures are withheld — a zero here would be
          fabricated, not a real balance.
        </div>
      )}

      <StaleDataBanner className="mt-1" />

      {/* Review checklist / status panel — semi-monthly board walkthrough */}
      <section className="glass rounded-2xl p-5">
        <h2 className="text-sm font-semibold text-theme-muted uppercase tracking-wider mb-3">
          Board Review Checklist
        </h2>
        <p className="text-xs text-theme-muted mb-4">
          Live status of the items the Board confirms each meeting. Items wired
          to data update automatically; the rest are policy reminders to walk
          aloud.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ReviewItem
            label="QuickBooks connection"
            status={qboLoaded && qboConnected ? "ok" : "pending"}
            value={
              !qboLoaded
                ? "Not loaded — QuickBooks connection status unavailable"
                : qboConnected
                  ? `Connected${qbo?.connected_by ? ` (${qbo.connected_by})` : ""}`
                  : "Not connected — pending production approval"
            }
          />
          <ReviewItem
            label="Levy position (funds available)"
            status={levyStatus}
            value={levyValue}
          />
          <ReviewItem
            label={`${nextFy} budget approval progress`}
            status={
              approvedCommittees == null || totalCommittees == null
                ? "pending"
                : "warn"
            }
            value={
              approvedCommittees == null || totalCommittees == null
                ? "Pending — v2 budget not loaded"
                : committeeSignoffComplete
                  ? `${nextFy} v2 committee sign-off complete (${approvedCommittees}/${totalCommittees} committees) — formal Board adoption pending (agenda item)`
                  : `${nextFy} v2 revision built 06-JUL — formal Board adoption pending (agenda item)`
            }
          />
          <ReviewItem
            label="Unbudgeted board-approved items"
            status={
              !trackingLoaded
                ? "pending"
                : unbudgetedItems.length > 0
                  ? "warn"
                  : "ok"
            }
            value={
              !trackingLoaded
                ? "Not loaded — unbudgeted-item list unavailable"
                : unbudgetedItems.length > 0
                  ? `${unbudgetedItems.length} item${unbudgetedItems.length === 1 ? "" : "s"} · ${usd0(unbudgetedTotal)} (review against motions)`
                  : "None on record"
            }
          />
          <ReviewItem
            label="Year-end P-Card allocation deadline"
            status="info"
            value={`June 24 — ${close.pcardDate} (PWC year-end calendar deadline, ahead of fiscal close)`}
          />
          <ReviewItem
            label="PO close worklist"
            status="info"
            value={
              figures
                ? `${figures.openPoCount} open PO${figures.openPoCount === 1 ? "" : "s"} · ${usdC(figures.encumbrance)} encumbered — run down before close (${close.fiscalCloseDate})`
                : `Open-PO worklist not loaded — run down before close (${close.fiscalCloseDate})`
            }
          />
        </div>

        {/* FA.PL.001 compliance checklist — static policy items the Board
            confirms at a glance; wired where a data source exists. */}
        <div className="mt-5 border-t border-theme-subtle/50 pt-4">
          <h3 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-2">
            FA.PL.001 Compliance Checklist
          </h3>
          <ul className="space-y-1.5">
            <ComplianceItem
              label="Monthly financials reviewed (§12.1)"
              done={null}
            />
            <ComplianceItem
              label="Bank reconciliations current & independently reviewed (§10)"
              // An unloaded QBO status is unknown (○), not a blocked ✕ — the
              // false state asserts "we know QuickBooks isn't connected".
              done={!qboLoaded ? null : qboConnected ? null : false}
              hint={
                !qboLoaded
                  ? "QuickBooks status not loaded"
                  : qboConnected
                    ? "Confirm in QuickBooks"
                    : "Pending QuickBooks connection"
              }
            />
            <ComplianceItem
              label="Purchase approvals within FA.PL.001 §5.5 thresholds"
              done={null}
            />
            <ComplianceItem
              label="Budget vs actual reviewed (see §2 below)"
              done={true}
            />
            <ComplianceItem
              label={`${nextFy} budget on track for board approval`}
              done={
                approvedCommittees != null &&
                totalCommittees != null &&
                totalCommittees > 0
                  ? committeeSignoffComplete
                  : null
              }
              hint={
                approvedCommittees != null && totalCommittees != null
                  ? committeeSignoffComplete
                    ? `${nextFy} v2 committee sign-off complete (${approvedCommittees}/${totalCommittees}) — formal Board adoption pending`
                    : `formal Board adoption pending — v2 revision on the meeting agenda`
                  : undefined
              }
            />
            <ComplianceItem
              label="Unbudgeted board-approved items tied to a motion"
              done={
                !trackingLoaded
                  ? null
                  : unbudgetedItems.length === 0
                    ? true
                    : null
              }
              hint={
                !trackingLoaded
                  ? "list not loaded"
                  : unbudgetedItems.length > 0
                    ? `${unbudgetedItems.length} to confirm`
                    : undefined
              }
            />
          </ul>
          <p className="text-xs text-theme-muted mt-3">
            A null / "confirm" state means there's no automated data source yet
            — the Board confirms it verbally and it'll wire up as QuickBooks and
            the reconciliation feed come online. No item here fabricates a
            value.
          </p>
        </div>
      </section>

      {/* Cash position */}
      <section className="glass rounded-2xl p-5">
        <h2 className="text-sm font-semibold text-theme-muted uppercase tracking-wider mb-3">
          1 · Cash Position
        </h2>
        {figures ? (
          <CashPositionFigures figures={figures} curFy={curFy} />
        ) : (
          <NotLoadedNotice
            title="Cash position not loaded"
            detail={
              summaryError
                ? "The levy summary failed to load. No figures are shown — a zero here would be fabricated, not a real balance."
                : "Waiting on the levy summary. Figures appear only once the real position is returned."
            }
          />
        )}
        {allObjectAvail != null && exprOnlyAvail != null && (
          <p className="mt-4 text-xs leading-snug text-theme-secondary border-t border-theme-subtle/50 pt-3">
            <span className="font-medium text-theme-primary">
              Funds available:
            </span>{" "}
            {usd0(allObjectAvail)} (all-object) · {usd0(exprOnlyAvail)}{" "}
            (expenditure-only / BCEA)
            {revenueGap != null ? (
              <>
                {" "}
                — difference ≈ {usdC(revenueGap)} budgeted-but-uncollected
                recovery revenue.
              </>
            ) : (
              "."
            )}
          </p>
        )}
      </section>

      {/* FY26 by year */}
      <section className="glass rounded-2xl p-5">
        <h2 className="text-sm font-semibold text-theme-muted uppercase tracking-wider mb-3">
          2 · Budget vs Actual by Fiscal Year
        </h2>
        {history == null ? (
          <NotLoadedNotice
            title="Budget-vs-actual history not loaded"
            detail="No rows are shown — an empty table would read as 'no history', which is not what the source said."
          />
        ) : (
          <table className="w-full text-sm">
            <caption className="sr-only">
              Budget versus actual by fiscal year
            </caption>
            <thead>
              <tr className="border-b border-theme-subtle text-theme-muted text-xs uppercase tracking-wider">
                <th scope="col" className="text-left py-2">
                  Fiscal Year
                </th>
                <th scope="col" className="text-right py-2">
                  Budget
                </th>
                <th scope="col" className="text-right py-2">
                  Actual
                </th>
                <th scope="col" className="text-right py-2">
                  <span className="inline-flex items-center gap-1">
                    Variance <InfoTip term="variance" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {fyRows.map((r) => {
                const v = r.actual - r.budget;
                return (
                  <tr
                    key={r.fy}
                    className="hover:bg-theme-overlay/50 transition-colors"
                  >
                    <td className="py-1.5 text-theme-primary font-medium">
                      {r.fy}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-theme-secondary">
                      {usd0(r.budget)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-theme-secondary">
                      {usd0(r.actual)}
                    </td>
                    <td
                      className={`py-1.5 text-right tabular-nums ${v > 0 ? "text-status-red" : "text-status-green"}`}
                    >
                      {v >= 0 ? "+" : ""}
                      {usd0(v)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* FY27 proposed budget (v2) — adoption pending */}
      <section className="glass rounded-2xl p-5">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold text-theme-muted uppercase tracking-wider">
            3 · {nextFy} Proposed Budget (v2) by Committee / Office
          </h2>
          <span className="text-xs text-theme-muted">
            v2 revision (built 06-JUL, adoption pending) · recurring operating
          </span>
        </div>
        {fy27V2 == null ? (
          <NotLoadedNotice
            title={`${nextFy} proposed budget (v2) not loaded`}
            detail={`No committee rows, no total and no comparison against the ${usd0(allocationTarget)} county base — a zero total here would read as a real proposal.`}
          />
        ) : (
          <table className="w-full text-sm">
            <caption className="sr-only">Proposed budget by committee</caption>
            <tbody>
              {committeeRows.map((c) => (
                <tr
                  key={c.committee}
                  className="hover:bg-theme-overlay/50 transition-colors"
                >
                  <td className="py-1.5 text-theme-primary">{c.committee}</td>
                  <td className="py-1.5 text-right tabular-nums font-medium text-theme-secondary">
                    {usd0(c.adopted)}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-theme-subtle font-bold">
                <td className="py-2 text-theme-primary">
                  Total Proposed {nextFy} (recurring)
                </td>
                <td className="py-2 text-right tabular-nums text-dcvfd-accent">
                  {usd0(totalAdopted)}
                </td>
              </tr>
              <tr>
                <td className="py-1 text-theme-muted text-xs">
                  vs {usd0(allocationTarget)} county base appropriation
                </td>
                <td
                  className={`py-1 text-right tabular-nums text-xs font-semibold ${gap > 0 ? "text-status-red" : "text-status-green"}`}
                >
                  {gap >= 0 ? "+" : ""}
                  {usd0(gap)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </section>

      <p className="text-xs text-theme-muted">
        Authoritative from PWC Oracle/Mobius. {nextFy} figures are the PROPOSED
        v2 budget (06-JUL-2026 revision, committee-reviewed; formal Board
        adoption pending), served live from the v2 budget tables — recurring
        operating shown here; capital, funded projects and FY26 open-PO
        carryover are separate layers on the {nextFy} Budget page. Revenue is
        tracked separately and never summed into available (cardinal rule).
      </p>

      {/* Running footer — print/PDF only. */}
      <div className="print-footer">
        Dale City Volunteer Fire Department · Confidential — Board Review
        {summary?.as_of_date ? ` · as of ${summary.as_of_date}` : ""}
      </div>
    </PageShell>
  );
}

// ── Packet figures (C-4: derived ONLY from a loaded summary) ─────────────────

export interface PacketFigures {
  available: number;
  encumbrance: number;
  openPoCount: number;
  carryoverEnc: number;
  carryoverCount: number;
  carryoverFund: string;
  expBudget: number;
  expConsumed: number;
  /** null when the budget isn't a positive number — "0% consumed" against an
   *  absent/zero budget is a fabricated clean state, not a computed ratio. */
  consumedPct: number | null;
  asOf: string | null;
}

/**
 * Derive the packet's cash-position figures from a LOADED summary. Takes a
 * non-null FinanceSummary by design: there is no "default" packet figure. The
 * caller renders an explicit not-loaded state when the summary is null, so this
 * function can never be the source of a fabricated zero.
 */
export function packetFigures(summary: FinanceSummary): PacketFigures {
  const expBudget = n(summary.expenditure?.budget);
  const expConsumed = n(summary.expenditure?.consumed);
  return {
    available: n(summary.funds_available),
    encumbrance: n(summary.open_pos?.encumbered),
    openPoCount: n(summary.open_pos?.count),
    carryoverEnc: n(summary.carryover_pos?.encumbered),
    carryoverCount: n(summary.carryover_pos?.count),
    carryoverFund: summary.carryover_pos?.fund ?? "2101",
    expBudget,
    expConsumed,
    consumedPct:
      expBudget > 0 ? Math.round((expConsumed / expBudget) * 100) : null,
    asOf: summary.as_of_date ?? null,
  };
}

/** The three §1 Cash Position figures — only ever rendered from loaded data. */
export function CashPositionFigures({
  figures,
  curFy,
}: {
  figures: PacketFigures;
  curFy: string;
}) {
  return (
    <div className="grid grid-cols-3 gap-4 text-center">
      <div>
        <div
          className={`text-3xl font-bold tabular-nums ${figures.available < 100_000 ? "text-status-red" : "text-theme-primary"}`}
        >
          {usd0(figures.available)}
        </div>
        <div className="text-xs text-theme-muted mt-1 inline-flex items-center gap-1">
          Discretionary available <InfoTip term="discretionary" />
        </div>
      </div>
      <div>
        <div className="text-3xl font-bold tabular-nums text-theme-primary">
          {usdC(figures.encumbrance)}
        </div>
        <div className="text-xs text-theme-muted mt-1 inline-flex items-center gap-1">
          Open-PO encumbrance ({figures.openPoCount}){" "}
          <InfoTip term="encumbrance" />
        </div>
        {figures.carryoverEnc > 0 && (
          <div className="text-xs text-theme-muted mt-1">
            + {usdC(figures.carryoverEnc)} FY26-carryover POs (
            {figures.carryoverCount}, fund {figures.carryoverFund}, own
            appropriation)
          </div>
        )}
      </div>
      <div>
        <div className="text-3xl font-bold tabular-nums text-theme-primary">
          {figures.consumedPct == null ? "—" : `${figures.consumedPct}%`}
        </div>
        <div className="text-xs text-theme-muted mt-1">
          {curFy} budget consumed
          {figures.consumedPct == null ? " (no budget reported)" : ""}
        </div>
      </div>
    </div>
  );
}

/** Explicit "we do not have this yet" panel — the honest stand-in for a figure
 *  we cannot compute. Never a zero, never an empty green space. */
export function NotLoadedNotice({
  title,
  detail,
}: {
  title: string;
  detail: string;
}) {
  return (
    <div
      role="status"
      className="rounded-lg border border-dashed border-theme-subtle px-4 py-6 text-center"
    >
      <div className="text-sm font-semibold text-theme-primary">{title}</div>
      <div className="text-xs text-theme-muted mt-1">{detail}</div>
    </div>
  );
}

// ── Board-packet CSV ──────────────────────────────────────────────────────────

export interface PacketCsvRow extends Record<string, unknown> {
  section: string;
  item: string;
  value: string;
  detail: string;
}

export interface BoardPacketCsvInput {
  summary: FinanceSummary | null;
  fyRows: { fy: string; budget: number; actual: number }[];
  committeeRows: { committee: string; adopted: number }[];
  totalAdopted: number;
  allocationTarget: number;
  curFy: string;
  nextFy: string;
}

/**
 * Build the board-packet CSV (filename + rows). Returns NULL when the levy
 * summary hasn't loaded — the export is refused rather than filled with zeros.
 *
 * When the summary carries no as_of_date the export says so, in the filename
 * and in a metadata row. It is NEVER stamped with today's date: a reader would
 * otherwise date the figures to the day the file was exported.
 */
export function buildBoardPacketCsv(
  input: BoardPacketCsvInput,
): { filename: string; rows: PacketCsvRow[] } | null {
  const {
    summary,
    fyRows,
    committeeRows,
    totalAdopted,
    allocationTarget,
    curFy,
    nextFy,
  } = input;
  if (!summary) return null;

  const f = packetFigures(summary);
  const asOf = f.asOf;
  const rows: PacketCsvRow[] = [];

  rows.push({
    section: "Packet Metadata",
    item: "Levy figures as of",
    value: asOf ?? "UNKNOWN",
    detail: asOf
      ? "source as_of_date"
      : "source as_of_date missing — these figures carry NO as-of date; do not infer one from the export date",
  });

  // 1 · Cash Position
  rows.push(
    {
      section: "Cash Position",
      item: "Discretionary available",
      value: String(Math.round(f.available)),
      detail: asOf ? `as of ${asOf}` : "as-of date unavailable",
    },
    {
      section: "Cash Position",
      item: "Open-PO encumbrance (operating fund)",
      value: String(Math.round(f.encumbrance)),
      detail: `${f.openPoCount} open POs`,
    },
    {
      section: "Cash Position",
      item: `${curFy} budget consumed (%)`,
      value: f.consumedPct == null ? "unavailable" : String(f.consumedPct),
      detail:
        f.consumedPct == null
          ? `no positive budget reported (${Math.round(f.expConsumed)} consumed)`
          : `${Math.round(f.expConsumed)} of ${Math.round(f.expBudget)}`,
    },
  );
  if (f.carryoverEnc > 0) {
    rows.push({
      section: "Cash Position",
      item: "FY26-carryover PO encumbrance",
      value: String(Math.round(f.carryoverEnc)),
      detail: `${f.carryoverCount} POs · fund ${f.carryoverFund}, own appropriation (not summed into operating)`,
    });
  }

  // 2 · Budget vs Actual by Fiscal Year
  for (const r of fyRows) {
    rows.push({
      section: "Budget vs Actual by FY",
      item: r.fy,
      value: String(Math.round(r.actual)),
      detail: `budget ${Math.round(r.budget)} · variance ${Math.round(r.actual - r.budget)}`,
    });
  }

  // 3 · FY27 Proposed Budget (v2) by Committee / Office
  for (const c of committeeRows) {
    rows.push({
      section: `${nextFy} Proposed (v2) by Committee`,
      item: c.committee,
      value: String(Math.round(c.adopted)),
      detail:
        "proposed (committee-reviewed; Board adoption pending) v2 (06-JUL-2026) · recurring operating",
    });
  }
  rows.push(
    {
      section: `${nextFy} Proposed (v2) by Committee`,
      item: `Total Proposed ${nextFy} (recurring)`,
      value: String(Math.round(totalAdopted)),
      detail: "capital / projects / PO carryover are separate layers",
    },
    {
      section: `${nextFy} Proposed (v2) by Committee`,
      item: "County base appropriation",
      value: String(allocationTarget),
      detail: `gap ${Math.round(totalAdopted - allocationTarget)}`,
    },
  );

  const stamp = asOf ? asOf.replace(/[^0-9-]/g, "") : "as-of-unknown";
  return { filename: `dcvfd-board-packet-${stamp}`, rows };
}

// ── Review-panel helpers ──────────────────────────────────────────────────────

type ReviewStatus = "ok" | "warn" | "pending" | "info";

// pending = "not wired up yet" (benign, not an active problem) — mapped to the
// same neutral tone as `info`, per the app-wide severity convention (green =
// good / amber = watch / red = act now; see PageIntro.tsx). Reserve red for
// genuine "act now" states only, matching FinanceAlerts' StatusLegend.
const REVIEW_DOT: Record<ReviewStatus, string> = {
  ok: "bg-status-green",
  warn: "bg-status-amber",
  pending: "bg-theme-muted",
  info: "bg-theme-muted",
};
const REVIEW_SR: Record<ReviewStatus, string> = {
  ok: "OK",
  warn: "Needs attention",
  pending: "Pending",
  info: "Reminder",
};

/** One live status row in the Board Review checklist. */
function ReviewItem({
  label,
  status,
  value,
}: {
  label: string;
  status: ReviewStatus;
  value: string;
}) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-theme-subtle/50 bg-theme-raised px-3 py-2.5">
      <span
        className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${REVIEW_DOT[status]}`}
        aria-hidden="true"
      />
      <span className="sr-only">{REVIEW_SR[status]}: </span>
      <div className="min-w-0">
        <div className="text-sm font-medium text-theme-primary">{label}</div>
        <div className="text-xs text-theme-secondary mt-0.5">{value}</div>
      </div>
    </div>
  );
}

/** A single FA.PL.001 compliance line. `done`: true = confirmed (✓), false =
 *  blocked / pending an external source (✕), null = confirm verbally (no data
 *  source yet, rendered as an open circle — never a fabricated ✓). */
function ComplianceItem({
  label,
  done,
  hint,
}: {
  label: string;
  done: boolean | null;
  hint?: string;
}) {
  const mark = done === true ? "✓" : done === false ? "✕" : "○";
  const markClass =
    done === true
      ? "text-status-green"
      : done === false
        ? "text-status-red"
        : "text-theme-muted";
  return (
    <li className="flex items-baseline gap-2 text-sm">
      <span
        className={`shrink-0 font-semibold ${markClass}`}
        aria-hidden="true"
      >
        {mark}
      </span>
      <span className="text-theme-secondary">
        {label}
        {hint ? <span className="text-theme-muted"> — {hint}</span> : null}
      </span>
    </li>
  );
}
